import { capitalCase } from 'change-case';

import { formatFinancialLineItemLabel } from '@/utilities/helpers/formatFinancialLineItemLabel';

export type RevenueSegmentKind = 'geography' | 'product';
export type FinancialStatementPeriodKind = 'annual' | 'quarter';
export type FinancialStatementGrowthKind = 'balance-sheet' | 'cash-flow' | 'income-statement';
export type FinancialGrowthSection = FinancialStatementGrowthKind | 'dividends';

export interface FinancialStatementRowSeries {
    key: string;
    label: string;
    lineItemAliases?: readonly string[];
    lineItemKey?: string;
    valuesByPeriod: Readonly<Record<string, number>>;
}

export interface FinancialStatementRowGroup {
    flatten?: boolean;
    key: string;
    label: string;
    parentLineItem: string;
    series: FinancialStatementRowSeries[];
}

export interface FinancialGridRow {
    [key: string]: unknown;
    depth: number;
    displayLabel: string;
    expandable: boolean;
    groupKey?: string;
    isGroupHeader: boolean;
    lineItemAliases?: readonly string[];
    lineItemKey?: string;
    lineItems: string;
}

interface SegmentMetadata {
    date?: string;
    fiscalYear?: string;
    period?: string;
}

const METADATA_KEYS = new Set([
    'acceptedDate',
    'calendarYear',
    'cik',
    'currency',
    'date',
    'filingDate',
    'fillingDate',
    'fiscalYear',
    'period',
    'reportedCurrency',
    'symbol',
    'year',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number(value.replaceAll(',', ''));

        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
};

const stringValue = (value: unknown) =>
    typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;

const normalizeDate = (value: string | undefined) => {
    if (!value) {
        return undefined;
    }

    return value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
};

const normalizeQuarter = (value: string | undefined) => value?.match(/Q([1-4])/i)?.[1];

const dateQuarter = (date: string | undefined) => {
    const month = Number(date?.slice(5, 7));

    return Number.isFinite(month) && month >= 1 && month <= 12 ? String(Math.ceil(month / 3)) : undefined;
};

const periodAliases = (metadata: SegmentMetadata, periodKind: FinancialStatementPeriodKind) => {
    const date = normalizeDate(metadata.date);
    const fiscalYear = metadata.fiscalYear ?? date?.slice(0, 4);
    const quarter = normalizeQuarter(metadata.period) ?? dateQuarter(date);
    const aliases = new Set<string>();

    if (date) {
        aliases.add(`date:${date}`);
    }

    if (fiscalYear) {
        aliases.add(`year:${fiscalYear}`);
        aliases.add(`label:${fiscalYear}`);
    }

    if (periodKind === 'quarter' && fiscalYear && quarter) {
        aliases.add(`fiscal:${fiscalYear}:Q${quarter}`);
        aliases.add(`label:Q${quarter} ${fiscalYear}`);
    }

    return [...aliases];
};

const metadataFromRecord = (record: Record<string, unknown>, fallbackDate?: string): SegmentMetadata => ({
    date: stringValue(record.date) ?? fallbackDate,
    fiscalYear: stringValue(record.fiscalYear) ?? stringValue(record.calendarYear) ?? stringValue(record.year),
    period: stringValue(record.period),
});

const mergeMetadata = (base: SegmentMetadata, override: SegmentMetadata): SegmentMetadata => ({
    date: override.date ?? base.date,
    fiscalYear: override.fiscalYear ?? base.fiscalYear,
    period: override.period ?? base.period,
});

const normalizeLabel = (value: string) => {
    const normalizedValue = value.replaceAll('_', ' ').replace(/\s+/g, ' ').trim();

    return normalizedValue === normalizedValue.toLocaleUpperCase() ? capitalCase(normalizedValue) : normalizedValue;
};

const flattenSegmentValues = (value: unknown, path: string[] = []): Array<{ label: string; value: number }> => {
    const numericValue = finiteNumber(value);

    if (numericValue !== undefined && path.length) {
        return [{ label: normalizeLabel(path.join(' › ')), value: numericValue }];
    }

    if (!isRecord(value)) {
        return [];
    }

    return Object.entries(value).flatMap(([key, nestedValue]) => flattenSegmentValues(nestedValue, [...path, key]));
};

const segmentNameFromRecord = (record: Record<string, unknown>) =>
    stringValue(record.segment) ??
    stringValue(record.segmentName) ??
    stringValue(record.name) ??
    stringValue(record.label);

const segmentValueFromRecord = (record: Record<string, unknown>) =>
    finiteNumber(record.revenue) ?? finiteNumber(record.value) ?? finiteNumber(record.amount);

const segmentPayloadFromRecord = (record: Record<string, unknown>) => {
    const payload = record.data ?? record.segments ?? record.segmentData;

    if (payload !== undefined) {
        return payload;
    }

    const numericFields = Object.fromEntries(
        Object.entries(record).filter(([key, value]) => !METADATA_KEYS.has(key) && finiteNumber(value) !== undefined),
    );

    return Object.keys(numericFields).length ? numericFields : undefined;
};

const responseItems = (response: unknown): unknown[] => {
    if (Array.isArray(response)) {
        return response;
    }

    if (!isRecord(response)) {
        return [];
    }

    if (Array.isArray(response.data)) {
        return response.data;
    }

    return [response];
};

export const normalizeRevenueSegmentResponse = ({
    kind,
    periodKind,
    response,
}: {
    kind: RevenueSegmentKind;
    periodKind: FinancialStatementPeriodKind;
    response: unknown;
}): FinancialStatementRowGroup => {
    const series = new Map<string, { label: string; valuesByPeriod: Record<string, number> }>();

    const addObservation = (label: string, value: number, metadata: SegmentMetadata) => {
        const normalizedLabel = normalizeLabel(label);

        if (!normalizedLabel) {
            return;
        }

        const seriesKey = normalizedLabel.toLocaleLowerCase();
        const current = series.get(seriesKey) ?? { label: normalizedLabel, valuesByPeriod: {} };

        for (const alias of periodAliases(metadata, periodKind)) {
            current.valuesByPeriod[alias] = value;
        }

        series.set(seriesKey, current);
    };

    const collectPayload = (payload: unknown, metadata: SegmentMetadata) => {
        if (Array.isArray(payload)) {
            payload.forEach((item) => {
                if (!isRecord(item)) {
                    return;
                }

                const label = segmentNameFromRecord(item);
                const value = segmentValueFromRecord(item);

                if (label && value !== undefined) {
                    addObservation(label, value, mergeMetadata(metadata, metadataFromRecord(item)));
                }
            });
            return;
        }

        for (const segment of flattenSegmentValues(payload)) {
            addObservation(segment.label, segment.value, metadata);
        }
    };

    for (const item of responseItems(response)) {
        if (!isRecord(item)) {
            continue;
        }

        const directLabel = segmentNameFromRecord(item);
        const directValue = segmentValueFromRecord(item);

        if (directLabel && directValue !== undefined) {
            addObservation(directLabel, directValue, metadataFromRecord(item));
            continue;
        }

        const payload = segmentPayloadFromRecord(item);

        if (payload !== undefined) {
            collectPayload(payload, metadataFromRecord(item));
            continue;
        }

        for (const [date, datePayload] of Object.entries(item)) {
            if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
                collectPayload(datePayload, { date });
            }
        }
    }

    return {
        key: kind,
        label: kind === 'product' ? 'Product Segments' : 'Geographic Segments',
        parentLineItem: 'revenue',
        series: [...series.entries()]
            .map(([key, value]) => ({ key: `${kind}:${key}`, ...value }))
            .filter((item) => Object.keys(item.valuesByPeriod).length > 0)
            .sort((a, b) => a.label.localeCompare(b.label)),
    };
};

const GROWTH_PARENT_ALIASES: Readonly<Record<string, string>> = {
    accountPayables: 'accountsPayable',
    netCashUsedForInvestingActivities: 'netCashUsedForInvestingActivites',
    otherFinancingActivities: 'otherFinancingActivites',
    otherInvestingActivities: 'otherInvestingActivites',
};

export interface FinancialGrowthLineItemPlacement {
    parentLineItems: readonly string[];
    section: FinancialGrowthSection;
}

/** Canonical destination for every metric formerly isolated in the Returns tab. */
export const FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS: Readonly<Record<string, FinancialGrowthLineItemPlacement>> = {
    assetGrowth: { parentLineItems: ['totalAssets'], section: 'balance-sheet' },
    bookValueperShareGrowth: {
        parentLineItems: ['totalStockholdersEquity', 'totalEquity'],
        section: 'balance-sheet',
    },
    debtGrowth: { parentLineItems: ['totalDebt', 'longTermDebt'], section: 'balance-sheet' },
    dividendsperShareGrowth: { parentLineItems: ['splitAdjustedAmount', 'amount'], section: 'dividends' },
    ebitgrowth: { parentLineItems: ['operatingIncome', 'ebitda'], section: 'income-statement' },
    epsdilutedGrowth: { parentLineItems: ['epsdiluted'], section: 'income-statement' },
    epsgrowth: { parentLineItems: ['eps'], section: 'income-statement' },
    fiveYDividendperShareGrowthPerShare: {
        parentLineItems: ['splitAdjustedAmount', 'amount'],
        section: 'dividends',
    },
    fiveYNetIncomeGrowthPerShare: { parentLineItems: ['netIncome'], section: 'income-statement' },
    fiveYOperatingCFGrowthPerShare: { parentLineItems: ['operatingCashFlow'], section: 'cash-flow' },
    fiveYRevenueGrowthPerShare: { parentLineItems: ['revenue'], section: 'income-statement' },
    fiveYShareholdersEquityGrowthPerShare: {
        parentLineItems: ['totalStockholdersEquity', 'totalEquity'],
        section: 'balance-sheet',
    },
    freeCashFlowGrowth: { parentLineItems: ['freeCashFlow'], section: 'cash-flow' },
    grossProfitGrowth: { parentLineItems: ['grossProfit'], section: 'income-statement' },
    inventoryGrowth: { parentLineItems: ['inventory'], section: 'balance-sheet' },
    netIncomeGrowth: { parentLineItems: ['netIncome'], section: 'income-statement' },
    operatingCashFlowGrowth: { parentLineItems: ['operatingCashFlow'], section: 'cash-flow' },
    operatingIncomeGrowth: { parentLineItems: ['operatingIncome'], section: 'income-statement' },
    rdexpenseGrowth: {
        parentLineItems: ['researchAndDevelopmentExpenses'],
        section: 'income-statement',
    },
    receivablesGrowth: { parentLineItems: ['netReceivables'], section: 'balance-sheet' },
    revenueGrowth: { parentLineItems: ['revenue'], section: 'income-statement' },
    sgaexpensesGrowth: {
        parentLineItems: ['sellingGeneralAndAdministrativeExpenses', 'generalAndAdministrativeExpenses'],
        section: 'income-statement',
    },
    tenYDividendperShareGrowthPerShare: {
        parentLineItems: ['splitAdjustedAmount', 'amount'],
        section: 'dividends',
    },
    tenYNetIncomeGrowthPerShare: { parentLineItems: ['netIncome'], section: 'income-statement' },
    tenYOperatingCFGrowthPerShare: { parentLineItems: ['operatingCashFlow'], section: 'cash-flow' },
    tenYRevenueGrowthPerShare: { parentLineItems: ['revenue'], section: 'income-statement' },
    tenYShareholdersEquityGrowthPerShare: {
        parentLineItems: ['totalStockholdersEquity', 'totalEquity'],
        section: 'balance-sheet',
    },
    threeYDividendperShareGrowthPerShare: {
        parentLineItems: ['splitAdjustedAmount', 'amount'],
        section: 'dividends',
    },
    threeYNetIncomeGrowthPerShare: { parentLineItems: ['netIncome'], section: 'income-statement' },
    threeYOperatingCFGrowthPerShare: { parentLineItems: ['operatingCashFlow'], section: 'cash-flow' },
    threeYRevenueGrowthPerShare: { parentLineItems: ['revenue'], section: 'income-statement' },
    threeYShareholdersEquityGrowthPerShare: {
        parentLineItems: ['totalStockholdersEquity', 'totalEquity'],
        section: 'balance-sheet',
    },
    weightedAverageSharesDilutedGrowth: {
        parentLineItems: ['weightedAverageSharesDiluted', 'weightedAverageShsOutDil'],
        section: 'income-statement',
    },
    weightedAverageSharesGrowth: {
        parentLineItems: ['weightedAverageShares', 'weightedAverageShsOut'],
        section: 'income-statement',
    },
};

const normalizeFinancialLineItemKey = (lineItem: string) => lineItem.replace(/[^a-z0-9]/gi, '').toLowerCase();

const growthParentLineItem = (growthLineItem: string, parentLineItems: ReadonlySet<string>) => {
    if (!growthLineItem.startsWith('growth') || growthLineItem.length === 'growth'.length) {
        return undefined;
    }

    const stripped = growthLineItem.slice('growth'.length);
    const candidate = `${stripped.charAt(0).toLocaleLowerCase()}${stripped.slice(1)}`;
    const normalizedCandidate = normalizeFinancialLineItemKey(candidate);
    const aliasedCandidate = Object.entries(GROWTH_PARENT_ALIASES).find(
        ([source]) => normalizeFinancialLineItemKey(source) === normalizedCandidate,
    )?.[1];
    const candidates = [candidate, aliasedCandidate];

    if (candidate.endsWith('Ratio')) {
        candidates.push(candidate.slice(0, -'Ratio'.length));
    }

    const exactMatch = candidates.find((lineItem): lineItem is string =>
        Boolean(lineItem && parentLineItems.has(lineItem)),
    );

    if (exactMatch) {
        return exactMatch;
    }

    const parentLineItemByNormalizedKey = new Map(
        [...parentLineItems].map((lineItem) => [normalizeFinancialLineItemKey(lineItem), lineItem]),
    );

    return candidates
        .filter((lineItem): lineItem is string => Boolean(lineItem))
        .map((lineItem) => parentLineItemByNormalizedKey.get(normalizeFinancialLineItemKey(lineItem)))
        .find((lineItem): lineItem is string => Boolean(lineItem));
};

export const normalizeFinancialStatementGrowthResponse = ({
    consolidatedResponse,
    growthSection,
    parentLineItems,
    periodKind,
    response,
}: {
    consolidatedResponse?: unknown;
    growthSection?: FinancialGrowthSection;
    parentLineItems: string[];
    periodKind: FinancialStatementPeriodKind;
    response: unknown;
}): FinancialStatementRowGroup[] => {
    const availableParentLineItems = new Set(parentLineItems);
    const seriesByParent = new Map<
        string,
        Map<
            string,
            {
                key: string;
                label: string;
                lineItemAliases: Set<string>;
                lineItemKey: string;
                priorityByPeriod: Record<string, number>;
                valuesByPeriod: Record<string, number>;
            }
        >
    >();

    const collectResponse = (
        payload: unknown,
        priority: number,
        resolveParent: (lineItemKey: string) => string | undefined,
    ) => {
        for (const item of responseItems(payload)) {
            if (!isRecord(item)) {
                continue;
            }

            const metadata = metadataFromRecord(item);

            for (const [lineItemKey, rawValue] of Object.entries(item)) {
                const value = finiteNumber(rawValue);
                const parentLineItem = resolveParent(lineItemKey);

                if (value === undefined || !parentLineItem) {
                    continue;
                }

                const parentSeries = seriesByParent.get(parentLineItem) ?? new Map();
                const seriesIdentity = normalizeFinancialLineItemKey(formatFinancialLineItemLabel(lineItemKey));
                const current = parentSeries.get(seriesIdentity) ?? {
                    key: lineItemKey,
                    label: normalizeLabel(lineItemKey),
                    lineItemAliases: new Set<string>(),
                    lineItemKey,
                    priorityByPeriod: {},
                    valuesByPeriod: {},
                };

                if (current.lineItemKey !== lineItemKey) {
                    current.lineItemAliases.add(lineItemKey);
                }

                for (const alias of periodAliases(metadata, periodKind)) {
                    if ((current.priorityByPeriod[alias] ?? -1) <= priority) {
                        current.priorityByPeriod[alias] = priority;
                        current.valuesByPeriod[alias] = value;
                    }
                }

                parentSeries.set(seriesIdentity, current);
                seriesByParent.set(parentLineItem, parentSeries);
            }
        }
    };

    collectResponse(response, 1, (lineItemKey) => growthParentLineItem(lineItemKey, availableParentLineItems));

    if (growthSection) {
        collectResponse(consolidatedResponse, 0, (lineItemKey) => {
            const placement = FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS[lineItemKey];

            if (!placement || placement.section !== growthSection) {
                return undefined;
            }

            return placement.parentLineItems.find((parentLineItem) => availableParentLineItems.has(parentLineItem));
        });
    }

    return parentLineItems.flatMap((parentLineItem) => {
        const series = [...(seriesByParent.get(parentLineItem)?.values() ?? [])]
            .map(({ key, label, lineItemAliases, lineItemKey, valuesByPeriod }) => ({
                key,
                label,
                lineItemAliases: [...lineItemAliases],
                lineItemKey,
                valuesByPeriod,
            }))
            .filter((item) => Object.keys(item.valuesByPeriod).length > 0);

        return series.length
            ? [
                  {
                      flatten: true,
                      key: 'growth',
                      label: 'Growth',
                      parentLineItem,
                      series,
                  },
              ]
            : [];
    });
};

const statementPeriodAliases = (
    row: Record<string, unknown>,
    periodLabel: string,
    periodKind: FinancialStatementPeriodKind,
) => {
    const aliases = new Set<string>([`label:${periodLabel}`]);
    const metadata = metadataFromRecord(row);

    for (const alias of periodAliases(metadata, periodKind)) {
        aliases.add(alias);
    }

    return [...aliases];
};

const resolveSegmentValue = (
    series: FinancialStatementRowSeries,
    statementRow: Record<string, unknown>,
    periodLabel: string,
    periodKind: FinancialStatementPeriodKind,
) => {
    for (const alias of statementPeriodAliases(statementRow, periodLabel, periodKind)) {
        const value = series.valuesByPeriod[alias];

        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
    }

    return undefined;
};

const createBaseRow = ({
    dataByPeriod,
    expandable,
    key,
    periods,
}: {
    dataByPeriod: Record<string, Record<string, unknown>>;
    expandable: boolean;
    key: string;
    periods: string[];
}): FinancialGridRow => {
    const row: FinancialGridRow = {
        depth: 0,
        displayLabel: key,
        expandable,
        groupKey: expandable ? key : undefined,
        isGroupHeader: false,
        lineItemKey: key,
        lineItems: key,
    };

    periods.forEach((period) => {
        row[`items${period}`] = dataByPeriod[period]?.[key];
    });

    return row;
};

export const buildFinancialGridRows = ({
    data,
    expandedGroups,
    keys,
    periodKind,
    periods,
    financialRowGroups = [],
}: {
    data: Array<Record<string, unknown>>;
    expandedGroups: ReadonlySet<string>;
    keys: string[];
    periodKind: FinancialStatementPeriodKind;
    periods: string[];
    financialRowGroups?: FinancialStatementRowGroup[];
}): FinancialGridRow[] => {
    const dataByPeriod = Object.fromEntries(data.map((record) => [String(record.periodLabel), record]));
    const matchingGroups = financialRowGroups
        .map((group) => ({
            ...group,
            series: group.series.filter((segmentSeries) =>
                periods.some((period) => {
                    const statementRow = dataByPeriod[period];

                    return (
                        statementRow &&
                        resolveSegmentValue(segmentSeries, statementRow, period, periodKind) !== undefined
                    );
                }),
            ),
        }))
        .filter((group) => group.series.length > 0);

    return keys.flatMap((key) => {
        const lineItemGroups = matchingGroups.filter((group) => group.parentLineItem === key);
        const isExpandable = lineItemGroups.length > 0;
        const rows = [
            createBaseRow({
                dataByPeriod,
                expandable: isExpandable,
                key,
                periods,
            }),
        ];

        if (!isExpandable || !expandedGroups.has(key)) {
            return rows;
        }

        for (const group of lineItemGroups) {
            if (group.flatten) {
                for (const segmentSeries of group.series) {
                    const segmentRow: FinancialGridRow = {
                        depth: 1,
                        displayLabel: segmentSeries.label,
                        expandable: false,
                        isGroupHeader: false,
                        lineItemAliases: segmentSeries.lineItemAliases,
                        lineItemKey: segmentSeries.lineItemKey,
                        lineItems: `${key}:${segmentSeries.key}`,
                    };

                    periods.forEach((period) => {
                        const statementRow = dataByPeriod[period];
                        segmentRow[`items${period}`] = statementRow
                            ? resolveSegmentValue(segmentSeries, statementRow, period, periodKind)
                            : undefined;
                    });
                    rows.push(segmentRow);
                }
                continue;
            }

            const groupKey = `${key}:${group.key}`;
            rows.push({
                depth: 1,
                displayLabel: group.label,
                expandable: true,
                groupKey,
                isGroupHeader: true,
                lineItems: groupKey,
            });

            if (!expandedGroups.has(groupKey)) {
                continue;
            }

            for (const segmentSeries of group.series) {
                const segmentRow: FinancialGridRow = {
                    depth: 2,
                    displayLabel: segmentSeries.label,
                    expandable: false,
                    isGroupHeader: false,
                    lineItemAliases: segmentSeries.lineItemAliases,
                    lineItemKey: segmentSeries.lineItemKey,
                    lineItems: `${key}:${segmentSeries.key}`,
                };

                periods.forEach((period) => {
                    const statementRow = dataByPeriod[period];
                    segmentRow[`items${period}`] = statementRow
                        ? resolveSegmentValue(segmentSeries, statementRow, period, periodKind)
                        : undefined;
                });
                rows.push(segmentRow);
            }
        }

        return rows;
    });
};
